import * as R from "ramda";
import * as React from "react";
import { connect } from "react-redux";
import { Creators as AllCreators } from "../../../../actions";
import { IState } from "../../../../App/reducer";
import { PageContextProvider } from "../../../../context/numbered-pagination";
import { RequestState } from "../../../../reducers/course-author/course-author";
import { CourseChanges } from "../../../course-author/helpers/patches";
import { ICourseEditOperations, ICourseResolveDisplayResponse, IFullCourse } from "../../../course-author/types";
import Spinner from "../../../utils/Spinner";
import ChangesPagination from "./ChangesPagination";

const Creators = (AllCreators as any).App.CourseAuthor;

export interface IChangesTabStateProps {
  editors: Array<[string, ICourseResolveDisplayResponse]>;
  changes: CourseChanges;
  fetchingChanges: boolean;
  fetchingEditors: boolean;
}

export interface IChangesTabDispatchProps {
  fetchChanges: (count: number, offset: number) => void;
  resolve: (key: string, data: string[]) => void;
}

export interface IChangesTabOwnProps {
  course: IFullCourse;
  courseId: string;
  userId: string;
  editOperations: ICourseEditOperations;
}

export interface IChangesTabState {
  fetched: number;
  pageIndex: number;
  perPage: number;
}

export type IChangesTabProps = IChangesTabStateProps & IChangesTabDispatchProps & IChangesTabOwnProps;

type AdminList = Array<R.KeyValuePair<string, ICourseResolveDisplayResponse>>;
interface IAdminMap {
  [id: string]: ICourseResolveDisplayResponse;
}

export const ChangesTab: React.FC<IChangesTabProps> = props => {
  const { editors, fetchingChanges, fetchingEditors } = props;

  const [fetched, setFetched] = React.useState(0);
  const [perPage, setPerPage] = React.useState(50);
  const [pageIndex, setPageIndex] = React.useState(0);
  const [maxPreloadedPage, setMaxPreloadedPage] = React.useState(0);

  const fetchNextPage = (count: number) => {
    const newFetched = Math.min(fetched, props.changes.length);
    props.fetchChanges(count, newFetched);
    setFetched(newFetched + count);
  };

  const publishVersion = (versionId?: number) => () => props.editOperations.publishVersion(versionId);
  const unpublishVersion = (versionId?: number) => () => props.editOperations.unpublishVersion(versionId);

  const editorsObj = R.compose<AdminList, AdminList, IAdminMap>(
    R.fromPairs,
    R.map<R.KeyValuePair<string, ICourseResolveDisplayResponse>, R.KeyValuePair<string, ICourseResolveDisplayResponse>>(
      ([id, admin]) => [id.slice(4, 40), admin]
    )
  )(editors);

  const value = {
    maxPreloadedPage,
    pageIndex,
    perPage,
    setMaxPreloadedPage,
    setPageIndex,
    setPerPage,
  };

  React.useEffect(() => {
    if (props.changes.length < perPage) {
      fetchNextPage(perPage);
    }
  }, []);

  React.useEffect(() => {
    setPageIndex(0);
  }, [perPage]);

  return !fetchingChanges && !fetchingEditors ? (
    <PageContextProvider value={value}>
      <ChangesPagination
        changes={props.changes}
        course={props.course}
        editors={editorsObj}
        fetchNextPage={fetchNextPage}
        maxChanges={props.course.versionId}
        pageIndex={pageIndex}
        perPage={perPage}
        courseId={props.courseId}
        userId={props.userId}
        publishVersion={publishVersion}
        unpublishVersion={unpublishVersion}
      />
    </PageContextProvider>
  ) : (
    <Spinner />
  );
};

export default connect<IChangesTabStateProps, IChangesTabDispatchProps, IChangesTabOwnProps, IState>(
  (state, ownProps) => ({
    changes: state.App.CourseAuthor._.courseChanges || [],
    editors: state.App.CourseAuthor._.resolvedData.editors,
    fetchingChanges: state.App.CourseAuthor._.fetchCourseChangesRequestState === RequestState.PENDING,
    fetchingEditors: state.App.CourseAuthor._.resolveAuxiliaryRequestState === RequestState.PENDING,
  }),
  (dispatch, ownProps) => ({
    fetchChanges: (count: number, offset: number) =>
      dispatch(Creators.fetchCourseChanges(ownProps.course, ownProps.courseId, count, offset)),
    resolve: (key: string, requestData: string[]) => dispatch(Creators.resolveAuxiliaryData(key, requestData)),
  })
)(ChangesTab);
